import { gql } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { SelectableButton } from '@moda/om';
import { SizeSelectorFragment } from '../../generated/types/SizeSelectorFragment';
import { SizeInventoryFragment } from '../../generated/types/SizeInventoryFragment';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import './SizeSelector.scss';

export const SIZE_INVENTORY_FRAGMENT = gql`
  fragment SizeInventoryFragment on Inventory {
    sku
    size
    available
    canSell: can_sell
  }
`;

export const VARIANT_INVENTORY_FRAGMENT = gql`
  fragment VariantInventoryFragment on VariantData {
    color
    inventory {
      ...SizeInventoryFragment
    }
  }
  ${SIZE_INVENTORY_FRAGMENT}
`;

export const SIZE_SELECTOR_FRAGMENT = gql`
  fragment SizeSelectorFragment on Variant {
    otherVariants: other_variants_data {
      ...VariantInventoryFragment
    }
    masterVariant: master_variants_data {
      ...VariantInventoryFragment
    }
  }
  ${VARIANT_INVENTORY_FRAGMENT}
`;

export interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  activeVariantId: string;
  variant: SizeSelectorFragment;
  selected: SizeInventoryFragment | null;
  onSelect(option: SizeInventoryFragment): void;
}

export const SizeSelector: React.FC<Props> = ({
  className,
  variant,
  activeVariantId,
  selected,
  onSelect,
  ...rest
}) => {
  const activeVariant = getActiveVariantBy(variant, 'id', activeVariantId);
  const [selectedSize, selectSize] = useState<SizeInventoryFragment | null>(null);

  // When the selected variation changes; try to find the same size in that variation
  useEffect(() => {
    selectSize(
      activeVariant.inventory.find(item => item?.size === selected?.size) as SizeInventoryFragment
    );
  }, [activeVariant.inventory, selected]);

  // If we have the same size then select it
  useEffect(() => {
    selectedSize && onSelect(selectedSize);
  }, [onSelect, selectedSize]);

  return (
    <div className={classNames('SizeSelector', className)} {...rest}>
      {activeVariant.inventory.map((item, i) => {
        return (
          item && (
            <SelectableButton
              className="SizeSelector__button"
              key={item.sku || i}
              unavailable={!item.canSell}
              selected={item.sku === selectedSize?.sku}
              onClick={() => onSelect(item)}
            >
              {item.size}
            </SelectableButton>
          )
        );
      })}
    </div>
  );
};
